import { LocationPositionCard } from '@/components/modules/LocationPositionCard';
import { userLevelToKr } from '@/functions/user-level-to-kr';
import { useKakaoMap } from '@/hooks/useKakaoMap';
import type { Position, Site, User } from '@/types/site';

interface OverviewTabProps {
  site: Site;
  managers: User[];
  expandedMaps: { [key: string]: boolean };
  selectedPosition: number | null;

  toggleMap: (key: string) => void;
  setSelectedPosition: (position: number | null) => void;
}

export default function OverviewTab({
  site,
  managers,
  expandedMaps,
  selectedPosition,
  toggleMap,
  setSelectedPosition,
}: Readonly<OverviewTabProps>) {
  const { mapRef: wellMapRef } = useKakaoMap(
    true,
    site
      ? {
          positions: [
            {
              latitude: site.wellPosition.latitude,
              longitude: site.wellPosition.longitude,
              name: site.wellPosition.name,
              address: site.wellPosition.address,
              type: 'well' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.wellPosition.latitude,
            longitude: site.wellPosition.longitude,
          },
        }
      : undefined
  );

  // 배수지 지도들 (최대 3개까지 지원)
  const { mapRef: waterSupply0MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[0] && expandedMaps['waterSupply-0'],
    site?.waterSupplyPosition?.[0]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[0].latitude,
              longitude: site.waterSupplyPosition[0].longitude,
              name: site.waterSupplyPosition[0].name,
              address: site.waterSupplyPosition[0].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[0].latitude,
            longitude: site.waterSupplyPosition[0].longitude,
          },
        }
      : undefined
  );

  const { mapRef: waterSupply1MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[1] && expandedMaps['waterSupply-1'],
    site?.waterSupplyPosition?.[1]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[1].latitude,
              longitude: site.waterSupplyPosition[1].longitude,
              name: site.waterSupplyPosition[1].name,
              address: site.waterSupplyPosition[1].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[1].latitude,
            longitude: site.waterSupplyPosition[1].longitude,
          },
        }
      : undefined
  );

  const { mapRef: waterSupply2MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[2] && expandedMaps['waterSupply-2'],
    site?.waterSupplyPosition?.[2]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[2].latitude,
              longitude: site.waterSupplyPosition[2].longitude,
              name: site.waterSupplyPosition[2].name,
              address: site.waterSupplyPosition[2].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[2].latitude,
            longitude: site.waterSupplyPosition[2].longitude,
          },
        }
      : undefined
  );

  const getWaterSupplyMapRef = (index: number) => {
    switch (index) {
      case 0:
        return waterSupply0MapRef;
      case 1:
        return waterSupply1MapRef;
      case 2:
        return waterSupply2MapRef;
      default:
        return waterSupply0MapRef;
    }
  };

  const formatCapacity = (capacity: number) => {
    if (capacity >= 1000) {
      return `${(capacity / 1000).toFixed(1)}천 톤`;
    }
    return `${capacity} 톤`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-rows-1 gap-6">
      {/* 기본 정보 */}
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            기본 정보
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">현장 ID</span>
              <span className="text-sm text-gray-900">{site.siteId}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                사이트 ID
              </span>
              <span className="text-sm text-gray-900">{site.siteId}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">현장명</span>
              <span className="text-sm text-gray-900">{site.name}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                CDMA 번호
              </span>
              <span className="text-sm text-gray-900">{site.cdmaNumber}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                관정 주소
              </span>
              <span className="text-sm text-gray-900 text-right max-w-xs">
                {site.wellPosition.address}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                배수지 주소
              </span>
              <span className="text-sm text-gray-900 text-right max-w-xs">
                {(site.waterSupplyPosition ?? [])[0]?.address || '-'}
              </span>
            </div>
            {/* <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                관리사 주소
              </span>
              <span className="text-sm text-gray-900 text-right max-w-xs">
                {site.managementPosition?.address}
              </span>
            </div> */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                관리번호
              </span>
              <span className="text-sm text-gray-900">
                {site?.managementNumber || '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            시설 정보
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                관리분류
              </span>
              <span className="text-sm text-gray-900">
                {site.managementType}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                시설유형
              </span>
              <span className="text-sm text-gray-900">{site.facilityType}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                취수원분류
              </span>
              <span className="text-sm text-gray-900">
                {site.waterIntakeType}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                설치일자
              </span>
              <span className="text-sm text-gray-900">
                {site.installedDate}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                모터제원
              </span>
              <span className="text-sm text-gray-900">
                {site.motorSpecification}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                탱크 용량
              </span>
              <span className="text-sm text-gray-900">
                {site?.tankSize ? formatCapacity(site.tankSize) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                모터 누적시간
              </span>
              <span className="text-sm text-gray-900">00분</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                마지막 리셋
              </span>
              <span className="text-sm text-gray-900">00시</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            담당 관리자
          </h4>
          <div className="space-y-2">
            {managers.length > 0 ? (
              managers.map((user: User) => (
                <div key={user.id} className="flex items-center space-x-3 py-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                    <i className="ri-user-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {user.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {userLevelToKr(user.userLevel)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">지정된 관리자가 없습니다.</p>
            )}
          </div>
        </div>
      </div>

      {/* 위치 정보 및 지도 */}
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-900">
              위치 정보 및 지도
            </h4>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">관정</span>
              </div>
              {/* {site.managementPosition && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-600">관리사</span>
                </div>
              )} */}
              {site.waterSupplyPosition &&
                site.waterSupplyPosition.length > 0 && (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">급수지</span>
                  </div>
                )}
            </div>
          </div>

          {/* 위치 정보 상세 카드들 */}
          <div className="space-y-4">
            {/* 취수원 정보 */}
            <LocationPositionCard
              color="blue"
              label="취수원"
              position={site.wellPosition}
              mapKey="well"
              isMapExpanded={expandedMaps['well']}
              mapRef={wellMapRef}
              onToggleMap={toggleMap}
              badge="주요 시설"
              detailToggle={{
                isExpanded: selectedPosition === 0,
                onToggle: () =>
                  setSelectedPosition(selectedPosition === 0 ? null : 0),
              }}
              showKakaoMapLink={true}
            />

            {/* 배수지 정보 */}
            {site.waterSupplyPosition &&
              site.waterSupplyPosition.length > 0 && (
                <div className="space-y-3">
                  {site.waterSupplyPosition.map(
                    (position: Position, index: number) => (
                      <LocationPositionCard
                        key={index}
                        color="green"
                        label={`배수지 ${index + 1}`}
                        position={position}
                        mapKey={`waterSupply-${index}`}
                        isMapExpanded={expandedMaps[`waterSupply-${index}`]}
                        mapRef={getWaterSupplyMapRef(index)}
                        onToggleMap={toggleMap}
                        badge="저장 시설"
                        detailToggle={{
                          isExpanded: selectedPosition === index + 2,
                          onToggle: () =>
                            setSelectedPosition(
                              selectedPosition === index + 2 ? null : index + 2
                            ),
                        }}
                        showKakaoMapLink={true}
                      />
                    )
                  )}
                </div>
              )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            가압장 모터
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between bg-white rounded-lg p-4 text-center border border-blue-200">
              <p className="text-m text-gray-600">활성화 모터 수</p>
              <p className="text-xl font-bold text-blue-600">
                {site.motorActivateCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            별칭 설정
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between bg-white rounded-lg p-4 text-center border border-blue-200">
              <p className="text-m text-gray-600">차단기 별칭</p>
              <p className="text-xl font-bold text-blue-600">
                {site.alias.circuitBreaker}
              </p>
            </div>
            <div className="flex justify-between bg-white rounded-lg p-4 text-center border border-green-200">
              <p className="text-m text-gray-600">압력센서 별칭</p>
              <p className="text-xl font-bold text-green-600">
                {site.alias.pressureSensor}
              </p>
            </div>
            <div className="flex justify-between bg-white rounded-lg p-4 text-center border border-green-200">
              <p className="text-m text-gray-600">외부 별칭</p>
              <p className="text-xl font-bold text-green-600">
                {site.alias.external}
              </p>
            </div>
            <div className="flex justify-between bg-white rounded-lg p-4 text-center border border-green-200">
              <p className="text-m text-gray-600">예비1 별칭</p>
              <p className="text-xl font-bold text-green-600">
                {site.alias.preparation}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
